import { Route, Switch, useLocation } from 'wouter';
import React, { lazy, Suspense, useEffect } from 'react';
import { Toaster } from '@/components/ui/toaster';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import { Loading } from '@/components/ui/Loading';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Helmet } from 'react-helmet-async';

// Lazy-loaded route components for code-splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const AIAgents = lazy(() => import('./pages/AIAgents'));
const AgentHosting = lazy(() => import('./pages/AgentHosting'));
const Resources = lazy(() => import('./pages/Resources'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Contact = lazy(() => import('./pages/Contact'));
const AdminPage = lazy(() => import('./pages/Admin'));
const NotFound = lazy(() => import('./pages/not-found'));

/**
 * Custom hook that scrolls the window to the top whenever the route location changes.
 */
const useScrollToTop = (): void => {
  const [location] = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function useGoogleAnalytics() {
  const [location] = useLocation();
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.gtag?.('event', 'page_view', {
        page_path: location,
      });
    }
  }, [location]);
}

/**
 * The main application component.
 * Sets up routing, layout structure (Navbar, Footer), and global components (Toaster).
 * Conditionally hides Navbar/Footer for the admin page.
 * Includes a hook to scroll to top on navigation.
 *
 * @returns The JSX element representing the entire application structure.
 */
const App = (): React.JSX.Element => {
  // Scroll to top on route change
  useScrollToTop();

  // Google Analytics SPA tracking
  useGoogleAnalytics();

  // Get current location to conditionally hide layout elements
  const [location] = useLocation();
  /** @constant isAdminPage - Flag indicating if the current route is the admin page. */
  const isAdminPage = location === '/admin';

  return (
    <ErrorBoundary>
      <Helmet>
        <script
          type="text/javascript"
          id="hs-script-loader"
          async
          defer
          src="//js-na2.hs-scripts.com/242480293.js"
        ></script>
      </Helmet>
      <div className="flex min-h-screen flex-col bg-[#1e0e2c]">
        {/* Conditionally render Navbar */}
        {!isAdminPage && <Navbar />}

        {/* Main content area that grows to fill space */}
        <main className="flex flex-grow flex-col">
          <Suspense fallback={<Loading />}>
            <Switch>
              {/* Define application routes */}
              <Route path="/" component={HomePage} />
              <Route path="/home-old" component={HomePage} />
              <Route path="/ai-agents" component={AIAgents} />
              <Route path="/agent-hosting" component={AgentHosting} />
              <Route path="/resources" component={Resources} />
              <Route path="/about" component={About} />
              <Route path="/services" component={Services} />
              <Route path="/portfolio" component={Portfolio} />
              <Route path="/contact" component={Contact} />
              <Route path="/contact/" component={Contact} />
              <Route path="/admin" component={AdminPage} />
              {/* Fallback route for 404 */}
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </main>

        {/* Conditionally render Footer */}
        {!isAdminPage && <Footer />}

        {/* Global Toaster component for notifications */}
        <Toaster />
      </div>
    </ErrorBoundary>
  );
};

export default App;
